import { css } from '@emotion/react'
import { HFlow, isEqual, Theme, useTheme, VFlow } from 'bold-ui'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { Breadcrumb } from 'components/breadcrumb/Breadcrumb'
import { createDecorator, Form, FormRenderProps } from 'components/form'
import { PageContent } from 'components/layout/PageContent'
import { TitleGroup } from 'components/TitleGroup'
import { useAuditCancelarBuscaCidadaoListagemMutation } from 'graphql/hooks.generated'
import {
  AcompanhamentoCondicaoSaudeTematicaEnum,
  ArquivoTemporarioIdentificadorEnum,
  ArquivoTemporarioStatusEnum,
} from 'graphql/types.generated'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { metaPath } from 'util/metaPath'
import { RelatoriosRecentes } from 'view/arquivo/RelatoriosRecentes'

import { AcompanhamentoCondicaoSaudeForm } from './AcompanhamentoCondicaoSaudeForm'
import { createAcompanhamentoCondicaoSaudeCalculations } from './calculator-acompanhamentoCondicaoSaude'
import { AcompanhamentoCondicaoSaudeTablePanel } from './components/table/AcompanhamentoCondicaoSaudeTablePanel'
import { AcompanhamentoCondicaoSaudeProvider } from './context/AcompanhamentoCondicaoSaudeContext'
import { convertAcompanhamentoCondicaoSaudeModelToQueryInput } from './converter-acompanhamentoCondicaoSaude'
import { useBuscaCidadaoListagem } from './hooks/useBuscaCidadaoListagem'
import {
  AcompanhamentoCondicaoSaudeFormModel,
  defaultFilterAcompanhamentoCondicaoSaudeByTematica,
} from './model/model-acompanhamentoCondicaoSaude'
import { acompanhamentoCondicaoSaudeValidator as validateForm } from './validator-acompanhamentoCondicaoSaude'

const decorators = [
  createDecorator(...createAcompanhamentoCondicaoSaudeCalculations(metaPath<AcompanhamentoCondicaoSaudeFormModel>())),
]

export default function AcompanhamentoCondicaoSaudeView() {
  const [currentFilter, setCurrentFilter] = useState<AcompanhamentoCondicaoSaudeFormModel>()

  const { acesso } = useAcessoLotacaoOrEstagio()

  const theme = useTheme()
  const styles = createStyles(theme)

  const [shouldRefetch, setShouldRefetch] = useState(false)
  const [statusRelatorio, setStatusRelatorio] = useState<ArquivoTemporarioStatusEnum>()

  const [listaTematica, setListaTematica] = useState<AcompanhamentoCondicaoSaudeTematicaEnum>()

  const {
    cidadaos,
    ciapsConsiderados,
    cidsConsiderados,
    executeQuery,
    loading,
    aborted,
    abortQuery,
  } = useBuscaCidadaoListagem(listaTematica)
  const [auditCancelarQuery] = useAuditCancelarBuscaCidadaoListagemMutation()

  const identificadorModuloArquivo = ArquivoTemporarioIdentificadorEnum.RELATORIO_ACOMPANHAMENTO_CONDICAO_SAUDE

  const contextValue = useMemo(
    () => ({
      tematica: listaTematica,
      ciapsConsiderados: ciapsConsiderados ?? [],
      cidsConsiderados: cidsConsiderados ?? [],
    }),
    [listaTematica, ciapsConsiderados, cidsConsiderados]
  )

  useEffect(() => {
    if (currentFilter) {
      executeQuery({
        variables: {
          input: convertAcompanhamentoCondicaoSaudeModelToQueryInput(currentFilter),
        },
      })
    }
  }, [currentFilter, executeQuery])

  const handleSubmit = (formValues: AcompanhamentoCondicaoSaudeFormModel) => {
    if (!isEqual(formValues, currentFilter) || aborted) {
      setCurrentFilter({ ...formValues })
    }
  }

  const handleAbortQuery = () => {
    auditCancelarQuery({
      variables: {
        input: convertAcompanhamentoCondicaoSaudeModelToQueryInput(currentFilter),
      },
    })
    abortQuery()
  }

  const renderForm = (formProps: FormRenderProps<AcompanhamentoCondicaoSaudeFormModel>) => {
    return (
      <AcompanhamentoCondicaoSaudeProvider value={contextValue}>
        <Fragment>
          <Breadcrumb title='Acompanhamento de condições de saúde' />
          <VFlow style={styles.pageContent}>
            <PageContent type='filled'>
              <HFlow justifyContent='space-between' style={styles.titleContent}>
                <TitleGroup title='Acompanhamento de condições de saúde' />
                <RelatoriosRecentes
                  setShouldRefetch={setShouldRefetch}
                  identificadorModuloArquivo={identificadorModuloArquivo}
                  setStatusRelatorio={setStatusRelatorio}
                />
              </HFlow>
              <AcompanhamentoCondicaoSaudeForm
                setListaTematica={setListaTematica}
                loading={loading}
                abortQuery={handleAbortQuery}
                {...formProps}
              />
            </PageContent>
          </VFlow>
          <PageContent>
            <AcompanhamentoCondicaoSaudeTablePanel
              currentFilter={currentFilter}
              onChangeTableProps={setCurrentFilter}
              shouldRefetch={shouldRefetch}
              setShouldRefetch={setShouldRefetch}
              statusRelatorio={statusRelatorio}
              data={cidadaos}
              loading={loading}
              abortQuery={handleAbortQuery}
              {...formProps}
            />
          </PageContent>
        </Fragment>
      </AcompanhamentoCondicaoSaudeProvider>
    )
  }

  return (
    <Form<AcompanhamentoCondicaoSaudeFormModel>
      render={renderForm}
      onSubmit={handleSubmit}
      validate={validateForm}
      decorators={decorators}
      initialValues={{
        ...defaultFilterAcompanhamentoCondicaoSaudeByTematica[AcompanhamentoCondicaoSaudeTematicaEnum.GERAL],
        tematica: AcompanhamentoCondicaoSaudeTematicaEnum.GERAL,
        unidadeResponsavelId: acesso?.unidadeSaude?.id,
        equipeResponsavel: acesso?.equipe,
      }}
    />
  )
}

const createStyles = (theme: Theme) => ({
  titleContent: css`
    margin: 1rem 0;
  `,
  pageContent: css`
    border-bottom: 1px solid ${theme.pallete.divider};
  `,
})
